import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Spinner, Form, Modal, Carousel } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tripService, applicationService, UPLOADS_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Formik } from 'formik';
import * as Yup from 'yup';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import './TripDetailPage.css';

const TripDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applySuccess, setApplySuccess] = useState(false);
  const [waitingList, setWaitingList] = useState([]);
  
  useEffect(() => {
    const fetchTripDetails = async () => {
      try {
        setLoading(true);
        const response = await tripService.getTripById(id);
        console.log("Trip data received:", response.data);
        setTrip(response.data);
        
        // Fetch waiting list
        try {
          const waitingListResponse = await applicationService.getTripWaitingList(id);
          setWaitingList(waitingListResponse.data || []);
        } catch (err) {
          console.log('Could not fetch waiting list:', err);
          setWaitingList([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to load trip details:', err);
        setError('Failed to load trip details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTripDetails();
  }, [id]);

  const handleApply = async (values, { setSubmitting }) => {
    try {
      setApplyError(null);
      await tripService.applyForTrip(id, values);
      setApplySuccess(true);
      setTimeout(() => {
        setShowApplyModal(false);
        // Refresh trip data
        const refreshTrip = async () => {
          const response = await tripService.getTripById(id);
          setTrip(response.data);
        };
        refreshTrip();
      }, 2000);
    } catch (err) {
      setApplyError(err.response?.data?.message || 'Failed to apply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelApplication = async () => {
    try {
      if (!trip.userApplication) return;
      
      await applicationService.cancelApplication(trip.userApplication.id);
      
      // Refresh trip data
      const response = await tripService.getTripById(id);
      setTrip(response.data);
    } catch (error) {
      console.error('Error canceling application:', error);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" className="text-primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error || !trip) {
    return (
      <Container>
        <Alert variant="danger">
          {error || 'Trip not found'}
        </Alert>
        <Button variant="primary" onClick={() => navigate('/trips')}>
          Back to Trips
        </Button>
      </Container>
    );
  }

  // Application status checks
  const hasApplied = !!trip.userApplication;
  const isApproved = hasApplied && trip.userApplication?.status === 'approved';
  const isPending = hasApplied && trip.userApplication?.status === 'pending';
  const isRejected = hasApplied && trip.userApplication?.status === 'rejected';
  const isOnWaitingList = hasApplied && trip.userApplication?.status === 'waitingList';
  const isFull = (trip.approvedCount || 0) >= trip.capacity;
  const capacityPercentage = ((trip.approvedCount || 0) / trip.capacity) * 100;
  
  // Calculate trip duration
  const startDate = new Date(trip.startDate);
  const endDate = new Date(trip.endDate);
  const durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  
  // Format dates
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get organizer initials for avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const applicationSchema = Yup.object().shape({
    message: Yup.string()
      .required('Please explain why you want to join this trip'),
    experience: Yup.string()
      .required('Please provide your relevant diving experience'),
    emergencyContact: Yup.string()
      .required('Emergency contact information is required'),
    specialRequirements: Yup.string()
  });

  return (
    <Container className="pb-5">
      <SEO 
        title={`${trip.name} - Diving Trip in ${trip.location} | DiveConnect`}
        description={trip.description.substring(0, 160) + '...'}
        keywords={`${trip.location}, scuba diving, dive trip, ${trip.difficulty} diving, underwater adventure, diving vacation`}
        image={trip.image ? `${UPLOADS_BASE_URL}/uploads/${trip.image}` : undefined}
        url={`https://dive-connect.com/trips/${trip._id}`}
        type="product"
      />
      
      <Breadcrumbs items={[
        { label: 'Trips', link: '/trips' },
        { label: trip.location },
        { label: trip.name }
      ]} />
      
      <Link to="/trips" className="back-button">
        <i className="bi bi-arrow-left"></i> Back to Trips
      </Link>

      {/* Image Carousel */}
      {(() => {
        const tripImages = trip.images && trip.images.length > 0 ? trip.images : [trip.image || 'default-trip.jpg'];
        
        return (
          <div className="trip-carousel-container mb-4">
            <Carousel interval={null} indicators={tripImages.length > 1} controls={tripImages.length > 1}>
              {tripImages.map((img, idx) => (
                <Carousel.Item key={idx}>
                  <img
                    className="d-block w-100 trip-carousel-image"
                    src={`${UPLOADS_BASE_URL}/uploads/${img}`}
                    alt={`${trip.name} - View ${idx + 1}`}
                    onError={(e) => { e.target.src = '/images/default-trip.jpg'; }}
                  />
                  {tripImages.length > 1 && (
                    <Carousel.Caption className="carousel-counter">
                      <span className="image-counter">{idx + 1} / {tripImages.length}</span>
                    </Carousel.Caption>
                  )}
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        );
      })()}
      
      <div className="trip-header">
        <Container>
          <div className="trip-title">{trip.name}</div>
          <div className="trip-location">
            <i className="bi bi-geo-alt-fill"></i> {trip.location}
          </div>
          <div className="d-flex align-items-center">
            <span className={`badge rounded-pill bg-${
              trip.status === 'upcoming' ? 'primary' : 
              trip.status === 'ongoing' ? 'success' : 
              trip.status === 'completed' ? 'secondary' : 'danger'
            } me-2`}>
              {trip.status.charAt(0).toUpperCase() + trip.status.slice(1)}
            </span>
            <span className="me-2">•</span>
            <span>{formatDate(trip.startDate)} - {formatDate(trip.endDate)}</span>
          </div>
        </Container>
      </div>
      
      <Row>
        <Col lg={8}>
          <div className="trip-content mb-4">
            <h3>Trip Details</h3>
            
            <div className="trip-detail-item">
              <div className="trip-detail-icon">
                <i className="bi bi-calendar-event"></i>
              </div>
              <div className="trip-detail-label">Dates</div>
              <div className="trip-detail-value">
                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
              </div>
            </div>
            
            <div className="trip-detail-item">
              <div className="trip-detail-icon">
                <i className="bi bi-clock"></i>
              </div>
              <div className="trip-detail-label">Duration</div>
              <div className="trip-detail-value">{durationDays} days</div>
            </div>
            
            <div className="trip-detail-item">
              <div className="trip-detail-icon">
                <i className="bi bi-award"></i>
              </div>
              <div className="trip-detail-label">Certification</div>
              <div className="trip-detail-value">
                {trip.minimumCertification} or higher
              </div>
            </div>
            
            <div className="trip-detail-item">
              <div className="trip-detail-icon">
                <i className="bi bi-people-fill"></i>
              </div>
              <div className="trip-detail-label">Capacity</div>
              <div className="trip-detail-value flex-grow-1">
                <div className="d-flex justify-content-between">
                  <span>{trip.approvedCount || 0}/{trip.capacity} Spots</span>
                  <span>{isFull ? 'Full' : 'Available'}</span>
                </div>
                <div className="capacity-bar">
                  <div 
                    className="capacity-progress" 
                    style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                  ></div>
                </div>
                {waitingList.length > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="bi bi-clock-history me-1"></i>
                    {waitingList.length} {waitingList.length === 1 ? 'person' : 'people'} on waiting list
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="trip-content">
            <h3>Description</h3>
            <div className="trip-description">
              {trip.description}
            </div>
            
            {trip.tags && trip.tags.length > 0 && (
              <div className="trip-tags">
                {trip.tags.map((tag, index) => (
                  <span key={index} className="trip-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Col>
        
        <Col lg={4}>
          <div className="status-card mb-4">
            <h4>Application Status</h4>
            
            {!currentUser ? (
              <div className="text-center py-3">
                <p>Please login to apply for this trip.</p>
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/login')}
                  className="mt-2"
                >
                  Login to Apply
                </Button>
              </div>
            ) : isAdmin && isAdmin() ? (
              <Alert variant="info">
                As an admin, you can view all applications in the admin dashboard.
              </Alert>
            ) : isApproved ? (
              <>
                <div className="status-badge status-badge-approved">
                  <i className="bi bi-check-circle-fill me-1"></i>
                  Approved
                </div>
                <p className="mb-4">Your application has been approved! You're all set for this trip.</p>
                <div className="fw-bold mb-2">Trip dates:</div>
                <p>{formatDate(trip.startDate)} - {formatDate(trip.endDate)}</p>
              </>
            ) : isPending ? (
              <>
                <div className="status-badge status-badge-pending">
                  <i className="bi bi-hourglass-split me-1"></i>
                  Pending Review
                </div>
                <p>Your application is under review. We'll notify you once it's processed.</p>
                {trip.userApplication?.adminComments && (
                  <div className="mb-3">
                    <div className="fw-bold mb-2">Note from organizer:</div>
                    <p className="fst-italic text-muted">{trip.userApplication.adminComments}</p>
                  </div>
                )}
                <Button
                  variant="outline-danger"
                  className="mt-3 w-100"
                  onClick={handleCancelApplication}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Cancel Application
                </Button>
              </>
            ) : isOnWaitingList ? (
              <>
                <div className="status-badge status-badge-warning">
                  <i className="bi bi-clock-history me-1"></i>
                  On Waiting List
                </div>
                <div className="waiting-list-position my-3 p-3 bg-light rounded text-center">
                  <div className="fs-5 fw-bold text-primary">Position #{trip.userApplication?.waitingListPosition}</div>
                  <div className="small text-muted mt-1">
                    {waitingList.length} {waitingList.length === 1 ? 'person' : 'people'} on waiting list
                  </div>
                </div>
                <p className="small text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  You'll be automatically notified if a spot opens up!
                </p>
                <Button
                  variant="outline-danger"
                  className="mt-2 w-100"
                  onClick={handleCancelApplication}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Leave Waiting List
                </Button>
              </>
            ) : isRejected ? (
              <>
                <div className="status-badge status-badge-rejected">
                  <i className="bi bi-x-circle-fill me-1"></i>
                  Not Approved
                </div>
                <p>Unfortunately, your application was not approved for this trip.</p>
                {trip.userApplication?.adminComments && (
                  <div>
                    <div className="fw-bold mb-2">Comments:</div>
                    <p className="fst-italic">{trip.userApplication.adminComments}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {isFull ? (
                  <Alert variant="warning">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    This trip is currently full. You can still apply to be on the waitlist.
                  </Alert>
                ) : null}
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => setShowApplyModal(true)}
                  disabled={trip.status !== 'upcoming'}
                >
                  <i className="bi bi-clipboard-check me-2"></i>
                  Apply for this Trip
                </Button>
                {trip.status !== 'upcoming' && (
                  <div className="text-muted small mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    You can only apply for upcoming trips.
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="status-card">
            <h4>Organized by</h4>
            <div className="organizer-info">
              <div className="organizer-avatar">
                {trip.createdBy?.name ? getInitials(trip.createdBy.name) : 'AD'}
              </div>
              <div className="organizer-details">
                <div className="organizer-name">{trip.createdBy?.name || 'Trip Organizer'}</div>
                <div className="organizer-email">{trip.createdBy?.email}</div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      
      {/* Application Modal */}
      <Modal 
        show={showApplyModal} 
        onHide={() => setShowApplyModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Apply for {trip.name}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            message: '',
            experience: '',
            emergencyContact: '',
            specialRequirements: ''
          }}
          validationSchema={applicationSchema}
          onSubmit={handleApply}
        >
          {({ handleSubmit, handleChange, values, touched, errors, isSubmitting }) => (
            <Form onSubmit={handleSubmit} className="application-form">
              <Modal.Body>
                {applyError && <Alert variant="danger">{applyError}</Alert>}
                {applySuccess && <Alert variant="success">Application submitted successfully!</Alert>}
                
                <Form.Group className="mb-3">
                  <Form.Label>Why do you want to join this trip?</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    isInvalid={touched.message && !!errors.message}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.message}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Relevant diving experience</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="experience"
                    value={values.experience}
                    onChange={handleChange}
                    isInvalid={touched.experience && !!errors.experience}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.experience}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact Information</Form.Label>
                  <Form.Control
                    type="text"
                    name="emergencyContact"
                    value={values.emergencyContact}
                    onChange={handleChange}
                    isInvalid={touched.emergencyContact && !!errors.emergencyContact}
                    placeholder="Name, relationship, phone number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.emergencyContact}
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Special Requirements or Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="specialRequirements"
                    value={values.specialRequirements}
                    onChange={handleChange}
                  />
                </Form.Group>
                
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting || applySuccess}>
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </Container>
  );
};

export default TripDetailPage;